import axios from 'axios';
import axiosRetry from 'axios-retry';
import { Metrics } from '@repo/observability';
import { v4 as uuidv4 } from 'uuid';

/**
 * 401 on these paths is not "session expired" — do not run refresh / onAuthError.
 * @param {string | undefined} url — axios `config.url` (often relative, e.g. `/auth/login`)
 */
function isNoRefresh401Path(url) {
    if (!url || typeof url !== 'string') return false;
    const path = url.split('?')[0];
    return (
        /\/auth\/(login|register|sign-up|forgot-password|reset-password|verify-email|verify|activate-account|logout|mfa)(\/|$)/i.test(
            path,
        ) || /\/auth\/refresh(\/|$)/i.test(path)
    );
}

export class HttpClientError extends Error {
    constructor(message, code, originalError = null) {
        super(message);
        this.name = 'HttpClientError';
        this.code = code;
        this.originalError = originalError;
        this.status = originalError?.response?.status;
    }
}

/**
 * @param {Object} config
 * @param {typeof import('opossum').default | null} [CircuitBreaker] — Node-only; omit in browser builds.
 */
export const createClient = (config = {}, CircuitBreaker = null) => {
    const {
        retries = 3,
        retryDelay = 1000,
        name = 'HttpClient',
        enableCircuitBreaker = false,
        onAuthError,
        validateSchema,
        ...axiosConfig
    } = config;

    const instance = axios.create({
        timeout: 20000,
        headers: {
            Accept: 'application/json',
        },
        ...axiosConfig,
    });

    axiosRetry(instance, {
        retries,
        retryDelay: (retryCount) => {
            console.warn(`[${name}] Retry attempt #${retryCount}`);
            return retryCount * retryDelay;
        },
        retryCondition: (error) =>
            axiosRetry.isNetworkOrIdempotentRequestError(error) || (error.response?.status >= 500),
    });

    instance.interceptors.request.use((req) => {
        const requestId = uuidv4();
        req.headers['X-Request-ID'] = requestId;
        req.metadata = { startTime: Date.now(), requestId };

        const isAuth = req.url?.includes('auth') || req.headers['Authorization'];
        console.debug(
            `[${name}][${requestId}] Request: ${req.method?.toUpperCase()} ${req.url}`,
            isAuth ? { headers: 'HIDDEN' } : {},
        );
        return req;
    });

    instance.interceptors.response.use(
        async (response) => {
            const { startTime, requestId } = response.config.metadata || {};
            const duration = startTime ? Date.now() - startTime : null;

            console.debug(
                `[${name}][${requestId}] Response: ${response.status} (${duration}ms) from ${response.config.url}`,
            );

            try {
                Metrics.get('http_request_duration_seconds')?.observe(
                    {
                        method: response.config.method?.toUpperCase(),
                        route: response.config.url,
                        status: response.status,
                        service: name,
                    },
                    duration / 1000,
                );
            } catch {
                /* browser Metrics is no-op */
            }

            const schema = response.config.schema || validateSchema;
            if (schema) {
                try {
                    response.data = await schema.parseAsync(response.data);
                } catch (zodError) {
                    console.error(`[${name}][${requestId}] Schema validation failed`, zodError);
                    throw new HttpClientError('Response data failed validation', 'VALIDATION_ERROR', {
                        response,
                        zodError,
                    });
                }
            }

            return response;
        },
        async (error) => {
            const originalRequest = error.config;
            const status = error.response?.status;

            const skipRefresh =
                originalRequest?.skipAuthRefresh === true || isNoRefresh401Path(originalRequest?.url);
            if (
                status === 401 &&
                onAuthError &&
                originalRequest &&
                !originalRequest._retry &&
                !skipRefresh
            ) {
                originalRequest._retry = true;
                console.warn(`[${name}] 401 detected. Attempting auth hook...`);
                try {
                    const newToken = await onAuthError();
                    if (newToken) {
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return instance(originalRequest);
                    }
                } catch (refreshErr) {
                    console.error(`[${name}] Auth hook failed.`, refreshErr);
                }
            }

            const url = originalRequest?.url || 'UNKNOWN_URL';
            if (status === 401 && originalRequest?._retry) {
                console.warn(`[${name}] Request Failed: 401 (after auth retry) on ${url}`, {
                    message: error.message,
                });
            } else if (status === 401 && isNoRefresh401Path(originalRequest?.url)) {
                console.warn(`[${name}] Request Failed: 401 on ${url}`, { message: error.message });
            } else {
                console.error(`[${name}] Request Failed: ${status || 'NETWORK'} on ${url}`, {
                    message: error.message,
                });
            }
            return Promise.reject(
                new HttpClientError(error.message, status ? 'HTTP_ERROR' : 'NETWORK_ERROR', error),
            );
        },
    );

    if (enableCircuitBreaker && CircuitBreaker) {
        const breaker = new CircuitBreaker(
            async (method, url, data, requestConfig) => {
                return instance.request({ method, url, data, ...requestConfig });
            },
            {
                timeout: 25000,
                errorThresholdPercentage: 50,
                resetTimeout: 30000,
            },
        );

        breaker.on('open', () => console.warn(`[${name}] Circuit Breaker OPEN. Fast failing.`));
        breaker.on('halfOpen', () => console.warn(`[${name}] Circuit Breaker HALF-OPEN. Testing upstream.`));
        breaker.on('close', () => console.info(`[${name}] Circuit Breaker CLOSED. Normal operation.`));

        instance.circuit = breaker;
    }

    return instance;
};
